import { useEffect, useRef, useState } from "react";
import LowPassFilter from "../../common/utils/LowPassFilter";
import MedianFilter from "../../common/utils/MedianFilter";

function useResizeObserver(ref) {
  const [size, setSize] = useState({ width: 0, height: 0 });
  
  useEffect(() => {
    if (!ref.current) return;

    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setSize({
        width: Math.floor(width),
        height: Math.floor(height),
      });
    });

    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return size;
}


const defaultAxisConfig = {
  yAxis: {
    enabled: true,
    line: {
      show: true,
      color: "rgba(255,255,255,0.4)",
      width: 1,
      offsetPx: 0,
    },
    labels: {
      show: true,
      count: 5,
      color: "#FFFFFF",
      font: "12px Arial",
      align: "right",
      paddingPx: 6,
      precision: 0,
      clamp: true,
      background: {
        show: false,
        color: "rgba(0,0,0,0.6)",
        paddingPx: 4,
        borderRadius: 2,
      },
    },
  },

  xAxis: {
    enabled: true,
    position: {
      mode: "zero", // zero | min | max | percent
      percent: 0.5,
      clamp: true,
    },
    line: {
      show: true,
      color: "rgba(255,255,255,0.4)",
      width: 1,
    },
    labels: {
      show: false,
      color: "#FFFFFF",
      font: "11px Arial",
      offsetPx: 4,
      formatter: "auto",
    },
  },
};


const mergeAxisConfig = (userConfig = {}) => {
  userConfig = userConfig || {};
  
  return {
    yAxis: {
      ...defaultAxisConfig.yAxis,
      ...userConfig.yAxis,
      line: {
        ...defaultAxisConfig.yAxis.line,
        ...userConfig?.yAxis?.line,
      },
      labels: {
        ...defaultAxisConfig.yAxis.labels,
        ...userConfig?.yAxis?.labels,
        background: {
          ...defaultAxisConfig.yAxis.labels.background,
          ...userConfig?.yAxis?.labels?.background,
        },
      },
    },
    xAxis: {
      ...defaultAxisConfig.xAxis,
      ...userConfig.xAxis,
      position: {
        ...defaultAxisConfig.xAxis.position,
        ...userConfig?.xAxis?.position,
      },
      line: {
        ...defaultAxisConfig.xAxis.line,
        ...userConfig?.xAxis?.line,
      },
      labels: {
        ...defaultAxisConfig.xAxis.labels,
        ...userConfig?.xAxis?.labels,
      },
    },
  };
};




export default function SnakeCanvas({
  name,
  nPointsPerWindow = 5000,
  headTailGapPx = 10,
  canvasResyncKey = 0,
  /** If set, updated after each draw batch: sweep phase in 0…1 (x head within plot width). */
  waveHeadPhaseReportRef,
  onWindowComplete,
  onWindowSizeChange,
  onGraphDrawerChange,
  getDataRange,
  getAxisConfig,
  leftPaddingPx = 50,
  medianFilterWindowSize = 0,
  lowPassFilterAlpha = 0,
  showFilterDebug = false,
}) {
  const containerRef = useRef(null);
  const gridCanvasRef = useRef(null);
  const rawWaveCanvasRef = useRef(null);
  const waveCanvasRef = useRef(null);


  const pixelsPerSampleRef = useRef(0);

  const callbacksRef = useRef({});
  callbacksRef.current = {
    onWindowComplete,
    onWindowSizeChange,
    onGraphDrawerChange,
    getDataRange,
    getAxisConfig,
  };

  const { width, height } = useResizeObserver(containerRef);

  // Internal mutable state (NO React re-render)
  const headXRef = useRef(leftPaddingPx + 1);
  const prevYRef = useRef(null);
  const rawHeadXRef = useRef(leftPaddingPx + 1);
  const rawPrevYRef = useRef(null);
  const runningRangeRef = useRef({ min: null, max: null });
  const lowPassFilterRef = useRef(null);
  const medianFilterRef = useRef(null);

  

  const mapYToCanvas = (yValue, yMin, yMax) => {
    if (yMax === yMin) return height / 2;
    const normalizedY = (yValue - yMin) / (yMax - yMin);
    return height - normalizedY * height;
  };


  // get y poinstion of X-axis
  const getXAxisY = (range, axis, height) => {
    const { mode, percent, clamp } = axis.position;
  
    let value;
    switch (mode) {
      case "min":
        value = range.min;
        break;
      case "max":
        value = range.max;
        break;
      case "percent":
        return height * percent;
      case "zero":
      default:
        value = 0;
    }
  
    let y = mapYToCanvas(value, range.min, range.max);
  
    if (clamp) {
      y = Math.max(0, Math.min(height, y));
    }
  
    return y;
  };

  // get x position of Y-axis
  const getYAxisX = (yAxis, leftPaddingPx) => {
    return leftPaddingPx + (yAxis?.line?.offsetPx || 0);
  };
  
  const drawXAxis = (ctx, range, axis, canvas, yAxisX) => {
    if (!axis.enabled || !axis.line.show) return;
  
    const y = getXAxisY(range, axis, canvas.height);
  
    ctx.save();
    ctx.strokeStyle = axis.line.color;
    ctx.lineWidth = axis.line.width;
  
    ctx.beginPath();
    ctx.moveTo(yAxisX, y);
    ctx.lineTo(canvas.width, y);
    ctx.stroke();
    ctx.restore();
  };

  const drawYAxis = (ctx, axis, canvas, leftPaddingPx) => {
    if (!axis.enabled || !axis.line.show) return;
  
    const x = leftPaddingPx + axis.line.offsetPx;
  
    ctx.save();
    ctx.strokeStyle = axis.line.color;
    ctx.lineWidth = axis.line.width;
  
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, canvas.height);
    ctx.stroke();
    ctx.restore();
  };
  

  


  const drawYLabels = (ctx, range, axis, canvas, leftPaddingPx) => {
    if (!axis.enabled || !axis.labels.show) return;
  
    const { count, precision, color, font, align, paddingPx, clamp } = axis.labels;
    

    const getFontSizePx = (font) => {
      const match = /(\d+)px/.exec(font);
      return match ? parseInt(match[1], 10) : 12;
    };

    ctx.save();
    ctx.fillStyle = color;
    ctx.font = font;
    ctx.textBaseline = "middle";
    ctx.textAlign = align === "right" ? "right" : "left";

    const fontSize = getFontSizePx(font);
    const margin = fontSize * 0.6; // 👈 dynamic clamp

    const usableHeight = canvas.height - margin * 2;
      const stepPx = usableHeight / (count - 1);
  
    for (let i = 0; i < count; i++) {
      // const value = range.min + ((range.max - range.min) * i) / (count-1);
  
      // let y = mapYToCanvas(value, range.min, range.max);
  
      // if (axis.labels.clamp) {
      //   y = Math.max(8, Math.min(canvas.height - 8, y));
      // }

      // 1️⃣ Pixel-first positioning (even spacing)
      let y = margin + i * stepPx;

      // 2️⃣ Map pixel → value
      const value = range.max - ((y - margin) / usableHeight) * (range.max - range.min);

      // 3️⃣ Optional safety clamp
      if (clamp) {
        y = Math.max(margin, Math.min(canvas.height - margin, y));
      }
  
      const x = align === "right" ? leftPaddingPx - paddingPx : leftPaddingPx + paddingPx;
  
      ctx.fillText(value.toFixed(precision), x, y);
    }
  
    ctx.restore();
  };
  
  


  useEffect(() => {
    lowPassFilterRef.current = new LowPassFilter(lowPassFilterAlpha)
      lowPassFilterAlpha > 0
        ? new LowPassFilter(lowPassFilterAlpha)
        : null;
    medianFilterRef.current =
      medianFilterWindowSize > 0
        ? new MedianFilter(medianFilterWindowSize)
        : null;
  }, [canvasResyncKey, lowPassFilterAlpha, medianFilterWindowSize]);

  useEffect(() => {
    if (!gridCanvasRef.current || !waveCanvasRef.current) return;
  
    // reset state
    headXRef.current = leftPaddingPx+1;
    prevYRef.current = null;
    rawHeadXRef.current = leftPaddingPx + 1;
    rawPrevYRef.current = null;
    runningRangeRef.current = { min: null, max: null };
  
    pixelsPerSampleRef.current =  (width - leftPaddingPx) / nPointsPerWindow;
  
    callbacksRef.current.onWindowSizeChange?.(width, height);
  
    const gridCtx = gridCanvasRef.current.getContext("2d");
    const waveCtx = waveCanvasRef.current.getContext("2d");
    const rawWaveCtx =
      showFilterDebug && rawWaveCanvasRef.current
        ? rawWaveCanvasRef.current.getContext("2d")
        : null;
  
    gridCtx.clearRect(0, 0, width, height);
    waveCtx.clearRect(0, 0, width, height);
    if (rawWaveCtx) {
      rawWaveCtx.clearRect(0, 0, width, height);
    }
  
    gridCtx.lineWidth = 1;
    waveCtx.lineWidth = 1.5;
    waveCtx.lineCap = "round";
    if (rawWaveCtx) {
      rawWaveCtx.lineWidth = 1;
      rawWaveCtx.lineCap = "round";
    }
  
    // draw static layer ONCE
    const range = callbacksRef.current.getDataRange?.();

    let axisConfig = defaultAxisConfig;
    if (callbacksRef.current.getAxisConfig) {
      axisConfig = callbacksRef.current.getAxisConfig();
    }

    const axis = mergeAxisConfig(axisConfig);

    const yAxisX = getYAxisX(axis.yAxis, leftPaddingPx);
    drawYAxis(gridCtx, axis.yAxis, {width, height}, leftPaddingPx);
    drawYLabels(gridCtx, range, axis.yAxis, {width, height}, leftPaddingPx);
    drawXAxis(gridCtx, range, axis.xAxis, {width, height}, yAxisX);
  
    let running = true;
  
    const draw = (points) => {
      if (!running || !points?.length) return;
  
      let headX = headXRef.current;
      let prevY = prevYRef.current;
      let rawHeadX = rawHeadXRef.current;
      let rawPrevY = rawPrevYRef.current;
  
      // clear tail ONLY on wave canvas
      waveCtx.clearRect(headX + 1, 0, headTailGapPx, height);
      if (rawWaveCtx) {
        rawWaveCtx.clearRect(rawHeadX + 1, 0, headTailGapPx, height);
      }
  
      for (const p of points) {
        if (showFilterDebug && rawWaveCtx && p.value != null) {
          const rawRange = callbacksRef.current.getDataRange?.();
          const rawY = mapYToCanvas(p.value, rawRange.min, rawRange.max);

          rawWaveCtx.strokeStyle = "rgba(255, 255, 255, 0.35)";

          if (rawPrevY != null) {
            rawWaveCtx.beginPath();
            rawWaveCtx.moveTo(rawHeadX, rawPrevY);
            rawHeadX += pixelsPerSampleRef.current;
            rawWaveCtx.lineTo(rawHeadX, rawY);
            rawWaveCtx.stroke();
          }

          rawPrevY = rawY;

          if (rawHeadX >= width) {
            rawHeadX = leftPaddingPx + 1;
            rawWaveCtx.clearRect(0, 0, headTailGapPx, height);
          }
        }

        let sampleValue = p.value;

        const lowPassFilter = lowPassFilterRef.current;
        const medianFilter = medianFilterRef.current;

        if (lowPassFilter && sampleValue != null) {
          sampleValue = lowPassFilter.filter(sampleValue);
        }

        if (medianFilter && sampleValue != null) {
          const filteredValue = medianFilter.filter(sampleValue);
          if (filteredValue == null) {
            continue;
          }
          sampleValue = filteredValue;
        }

        const range = callbacksRef.current.getDataRange?.();
        const y = mapYToCanvas(sampleValue, range.min, range.max);
  
        if (sampleValue != null) {
          runningRangeRef.current.min =
            runningRangeRef.current.min == null
              ? sampleValue
              : Math.min(runningRangeRef.current.min, sampleValue);
  
          runningRangeRef.current.max =
            runningRangeRef.current.max == null
              ? sampleValue
              : Math.max(runningRangeRef.current.max, sampleValue);
        }
  
        waveCtx.strokeStyle = p.color || "lime";
  
        if (prevY != null) {
          waveCtx.beginPath();
          waveCtx.moveTo(headX, prevY);
          headX += pixelsPerSampleRef.current;
          waveCtx.lineTo(headX, y);
          waveCtx.stroke();
        }
  
        prevY = y;
  
        // wrap
        if (headX >= width) {
          headX = leftPaddingPx+1;
  
          waveCtx.clearRect(0, 0, headTailGapPx, height);
  
          callbacksRef.current.onWindowComplete?.(
            runningRangeRef.current.min,
            runningRangeRef.current.max
          );
  
          runningRangeRef.current = { min: null, max: null };
  
          // redraw grid with updated range
          gridCtx.clearRect(0, 0, width, height);

          const newRange = callbacksRef.current.getDataRange?.();
          let axisConfig = defaultAxisConfig;
          if (callbacksRef.current.getAxisConfig) {
            axisConfig = callbacksRef.current.getAxisConfig();
          }

          const axis = mergeAxisConfig(axisConfig);
          const yAxisX = getYAxisX(axis.yAxis, leftPaddingPx);
          drawYAxis(gridCtx, axis.yAxis, {width, height}, leftPaddingPx);
          drawYLabels(gridCtx, newRange, axis.yAxis, {width, height}, leftPaddingPx);
          drawXAxis(gridCtx, newRange, axis.xAxis, {width, height}, yAxisX);
        }
      }
  
      headXRef.current = headX;
      prevYRef.current = prevY;
      if (showFilterDebug) {
        rawHeadXRef.current = rawHeadX;
        rawPrevYRef.current = rawPrevY;
      }
      if (waveHeadPhaseReportRef && width > leftPaddingPx) {
        waveHeadPhaseReportRef.current =
          (headX - leftPaddingPx) / (width - leftPaddingPx);
      }
    };
  
    callbacksRef.current.onGraphDrawerChange?.(draw);
  
    return () => {
      running = false;
      callbacksRef.current.onGraphDrawerChange?.(null);
    };
  }, [width, height, nPointsPerWindow, headTailGapPx, leftPaddingPx, canvasResyncKey, waveHeadPhaseReportRef, showFilterDebug]);
  

  if (width === 0 || height === 0) {
    return <div ref={containerRef} style={{ width: "100%", height: "100%", background:  "transparent" }}></div>;
  }



  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        height: "100%",
        position: "relative",
      }}
    >
      {/* Grid / Axes canvas */}
      <canvas
        ref={gridCanvasRef}
        width={width}
        height={height}
        style={{
          position: "absolute",
          inset: 0,
          zIndex: 1,
        }}
      />

      {showFilterDebug && (
        <canvas
          ref={rawWaveCanvasRef}
          width={width}
          height={height}
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 2,
          }}
        />
      )}
  
      {/* Waveform canvas */}
      <canvas
        ref={waveCanvasRef}
        width={width}
        height={height}
        style={{
          position: "absolute",
          inset: 0,
          zIndex: 3,
        }}
      />
    </div>
  );
  
}
